import { ICurrentWorklistDashboardHelper } from '@/Pages/GenericWorklist/Stores/CurrentWorklistDashboardHelper';
import { ICustomWorklistHelper } from '@/Pages/GenericWorklist/Stores/CustomWorklistHelper';
import { IWorklistStore } from '@/Pages/GenericWorklist/Stores/WorklistStore';
import { DobButton, DobTooltip } from '@dedalusdiit/dob-components';
import { observer } from 'mobx-react-lite';
import { MouseEventHandler, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IWorklistToDashboardDialogViewStore } from '../../Dialogs/WorklistToDashboardDialog/WorklistToDashboardDialogViewStore';
import { IWorklistSidebarStore } from '../WorklistSidebarStore';
import Styles from './WorklistSideBarFooter.module.scss';
import { WorklistSidebarFooterViewHelper } from './WorklistSidebarFooterViewHelper';

export interface WorklistSideBarBottomProps {
    currentWorklistDashboardStore: ICurrentWorklistDashboardHelper;
    customWorklistsStore: ICustomWorklistHelper;
    isLoading: boolean;
    worklistSidebarStore: IWorklistSidebarStore;
    worklistStore: IWorklistStore;
    worklistToDashboardDialogViewStore: IWorklistToDashboardDialogViewStore;
}

const {
    applyButton: applyButtonStyle,
    worklistSidebarFooter: worklistSidebarFooterStyle,
    extraFunctionality: extraFunctionalityStyle,
    worklistTools: worklistToolsStyle,
} = Styles;

const WorklistSideBarFooter = (props: Readonly<WorklistSideBarBottomProps>): React.ReactElement => {
    const { t } = useTranslation();

    const {
        currentWorklistDashboardStore,
        customWorklistsStore,
        worklistSidebarStore,
        worklistStore,
        worklistToDashboardDialogViewStore,
    } = props;

    const viewHelper = useMemo(() => new WorklistSidebarFooterViewHelper(currentWorklistDashboardStore, customWorklistsStore, worklistSidebarStore, worklistStore), [currentWorklistDashboardStore, customWorklistsStore, worklistSidebarStore, worklistStore]);
    const {
        addCustomWorklistButtonTooltipTextKey,
        deleteCustomWorklistButtonTooltipTextKey,
        editCustomWorklistButtonTooltipTextKey,
        isApplyDisabled,
        isClearDisabled,
        isDeleteCustomWorklistButtonDisabled,
        isEditCustomWorklistButtonAvailable,
        isEditCustomWorklistButtonDisabled,
        isRestoreDisabled,
        isAddCustomWorklistButtonDisabled,
        isWorklistDashboardButtonAvailable,
        isWorklistDashboardButtonDisabled,
        worklistDashboardButtonTooltipText,
        clearCurrentFilter,
        restoreActiveCustomWorklist,
    } = viewHelper;

    const applyOnClickHandler: MouseEventHandler<HTMLButtonElement> = (): void => {
        worklistSidebarStore.applyCurrentFilter();
    };

    const clearOnClickHandler: MouseEventHandler<HTMLButtonElement> = (): void => {
        clearCurrentFilter();
    };

    const restoreOnClickHandler = (): void => {
        restoreActiveCustomWorklist();
    };

    const saveOnClickHandler = (): void => {
        worklistSidebarStore.toggleSaveWorklistDialog(true);
    };

    const updateOnClickHandler = (): void => {
        worklistSidebarStore.toggleUpdateWorklistDialog(true);
    };

    const onDeleteClickHandler = (): void => {
        worklistSidebarStore.toggleDeleteModal(true);
    };

    return (
        <div className={worklistSidebarFooterStyle}>
            <DobButton className={applyButtonStyle} onClick={applyOnClickHandler} disabled={isApplyDisabled}>{t('Apply')}</DobButton>
            <div className={extraFunctionalityStyle}>
                <DobButton color="silent" onClick={restoreOnClickHandler} disabled={isRestoreDisabled}>{t('Restore')}</DobButton>
                <DobButton color="silent" onClick={clearOnClickHandler} disabled={isClearDisabled}>{t('Clear')}</DobButton>
            </div>

            <div className={worklistToolsStyle}>
                { isWorklistDashboardButtonAvailable && (
                    <DobTooltip content={t(worklistDashboardButtonTooltipText)}>
                        <DobButton
                            color={worklistToDashboardDialogViewStore.buttonColor}
                            outline={worklistToDashboardDialogViewStore.hasButtonOutline}
                            size="small"
                            icon="bar-chart-fill"
                            onClick={() => worklistToDashboardDialogViewStore.toggleIsDialogOpen()}
                            disabled={isWorklistDashboardButtonDisabled}
                        />
                    </DobTooltip>
                )}
                { customWorklistsStore.isDeleteCustomWorklistButtonAvailable && (
                    <DobTooltip content={t(deleteCustomWorklistButtonTooltipTextKey)}>
                        <DobButton
                            color="silent"
                            size="small"
                            icon="trash3-fill"
                            disabled={isDeleteCustomWorklistButtonDisabled}
                            onClick={onDeleteClickHandler}
                        />
                    </DobTooltip>
                )}
                { isEditCustomWorklistButtonAvailable && (
                    <DobTooltip content={t(editCustomWorklistButtonTooltipTextKey)}>
                        <DobButton
                            color="silent"
                            size="small"
                            icon="floppy2-fill"
                            disabled={isEditCustomWorklistButtonDisabled}
                            onClick={updateOnClickHandler}
                        />
                    </DobTooltip>
                )}
                <DobTooltip content={t(addCustomWorklistButtonTooltipTextKey)}>
                    <DobButton
                        color="silent"
                        size="small"
                        icon="plus"
                        iconSize="24px"
                        disabled={isAddCustomWorklistButtonDisabled}
                        onClick={saveOnClickHandler}
                    />
                </DobTooltip>
            </div>
        </div>
    );
};

export default observer(WorklistSideBarFooter);
